import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { 
  X, Send, Bot, User, Loader2, 
  Scale, Minimize2, Maximize2,
  HelpCircle, Trash2, Sparkles, Copy, Check
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const ROLE_SUGGESTIONS = {
  POLICE_OFFICER: {
    fr: [
      "Quelle est la duree maximale de garde a vue ?",
      "Quels sont mes droits en tant qu'OPJ lors d'une perquisition ?",
      "Procedure a suivre pour une interpellation",
      "Quand puis-je proceder a une arrestation sans mandat ?"
    ],
    en: ["Maximum custody duration?", "OPJ rights during search?", "Interpellation procedure?", "Arrest without warrant?"]
  },
  JUDGE: {
    fr: [
      "Conditions de la detention preventive",
      "Procedure de mise en liberte conditionnelle",
      "Competence des juridictions militaires",
      "Circonstances attenuantes selon le Code Penal"
    ],
    en: ["Preventive detention conditions?", "Conditional release procedure?", "Military court jurisdiction?"]
  },
  DEFAULT: {
    fr: [
      "Quelle est la duree maximale de garde a vue en RDC ?",
      "Comment deposer une plainte ?",
      "Quels sont les droits d'un detenu ?",
      "Quelle peine pour le vol a main armee ?",
      "Qu'est-ce que la corruption en droit congolais ?"
    ],
    en: ["Maximum custody duration in DRC?", "How to file a complaint?", "Detainee rights?", "Penalty for armed robbery?"]
  }
};

const getRoleSuggestions = (role, lang) => {
  if (['OPJ', 'POLICE_OFFICER', 'AGENT_POLICE', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL'].includes(role)) {
    return ROLE_SUGGESTIONS.POLICE_OFFICER[lang] || ROLE_SUGGESTIONS.POLICE_OFFICER.fr;
  }
  if (['JUGE_PAIX', 'SUBSTITUT_PROCUREUR', 'PROCUREUR_REPUBLIQUE', 'MAGISTRATE', 'JUDGE'].includes(role)) {
    return ROLE_SUGGESTIONS.JUDGE[lang] || ROLE_SUGGESTIONS.JUDGE.fr;
  }
  return ROLE_SUGGESTIONS.DEFAULT[lang] || ROLE_SUGGESTIONS.DEFAULT.fr;
};

const renderMessage = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const rendered = parts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={j} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      return part;
    });

    if (line.startsWith('### ')) return <h4 key={i} className="font-semibold text-sm mt-3 mb-1 text-foreground">{line.slice(4)}</h4>;
    if (line.startsWith('## ')) return <h3 key={i} className="font-bold text-base mt-3 mb-1 text-foreground">{line.slice(3)}</h3>;
    if (line.startsWith('# ')) return <h2 key={i} className="font-bold text-lg mt-3 mb-1 text-foreground">{line.slice(2)}</h2>;
    if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-sm list-disc leading-relaxed">{rendered.slice(0)}</li>;
    if (/^\d+\.\s/.test(line)) return <li key={i} className="ml-4 text-sm list-decimal leading-relaxed">{rendered}</li>;
    if (line.trim() === '') return <div key={i} className="h-2" />;
    return <p key={i} className="text-sm leading-relaxed">{rendered}</p>;
  });
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-background/50" title="Copier">
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-muted-foreground" />}
    </button>
  );
};

const LegalAIAssistant = () => {
  const { user, language } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const storedSession = localStorage.getItem('snigj_ai_session');
    if (storedSession) {
      setSessionId(storedSession);
      loadChatHistory(storedSession);
    } else {
      const newSession = `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSession);
      localStorage.setItem('snigj_ai_session', newSession);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChatHistory = async (sid) => {
    try {
      const response = await api.get(`/ai/chat-history/${sid}`);
      if (response.data?.messages) setMessages(response.data.messages);
    } catch { /* no history */ }
  };

  const handleSendMessage = async (text = inputMessage) => {
    if (!text.trim() || isLoading) return;
    const userMessage = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    try {
      const response = await api.post('/ai/chat', {
        message: text.trim(), session_id: sessionId, language,
        user_role: user?.role || 'CITIZEN', context: 'legal_assistance'
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response, timestamp: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: language === 'fr' ? "Desole, je rencontre des difficultes techniques. Veuillez reessayer." : "Sorry, technical difficulties. Please retry.",
        timestamp: new Date().toISOString(), isError: true
      }]);
      toast.error(language === 'fr' ? 'Erreur de communication' : 'Communication error');
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleClearChat = async () => {
    if (!window.confirm(language === 'fr' ? "Effacer l'historique ?" : 'Clear history?')) return;
    try {
      await api.delete(`/ai/chat-history/${sessionId}`);
      setMessages([]);
      const newSession = `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSession);
      localStorage.setItem('snigj_ai_session', newSession);
      toast.success(language === 'fr' ? 'Historique efface' : 'History cleared');
    } catch { /* ignore */ }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const suggestions = getRoleSuggestions(user?.role, language);

  // Floating action button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 group"
        data-testid="ai-assistant-btn"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-emerald-500/20 rounded-2xl blur-xl group-hover:bg-emerald-500/30 transition-all duration-500" />
          <div className="relative h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-105 transition-all duration-300 flex items-center justify-center">
            <Scale className="w-6 h-6" />
          </div>
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white shadow-sm">
            <span className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75" />
          </span>
        </div>
      </button>
    );
  }

  const panelBase = 'fixed z-50 shadow-2xl transition-all duration-300 overflow-hidden flex flex-col border border-border/50';
  const panelSize = isExpanded
    ? `${panelBase} bottom-0 right-0 w-full sm:w-[640px] h-full sm:h-[92vh] sm:bottom-3 sm:right-3 sm:rounded-2xl`
    : isMinimized
      ? `${panelBase} bottom-6 right-6 w-72 h-14 rounded-2xl`
      : `${panelBase} bottom-6 right-6 w-[400px] h-[600px] max-h-[85vh] rounded-2xl`;

  return (
    <div className={panelSize} style={{ background: 'var(--background)' }} data-testid="ai-assistant-panel">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b flex items-center justify-between" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(6,182,212,0.05))' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center shadow-sm shadow-emerald-500/20">
            <Scale className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight leading-none">
              {language === 'fr' ? 'Assistant Juridique' : 'Legal Assistant'}
            </h3>
            {!isMinimized && (
              <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                SNIGJ v2 - Droit congolais
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          {!isMinimized && (
            <>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={handleClearChat} title="Effacer">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => setIsExpanded(!isExpanded)}>
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => setIsMinimized(!isMinimized)}>
            {isMinimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => { setIsOpen(false); setIsExpanded(false); }}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages area */}
          <ScrollArea className="flex-1 px-4 py-3">
            {messages.length === 0 && (
              <div className="space-y-4">
                {/* Welcome card */}
                <div className="rounded-xl p-4 border border-emerald-200/50 dark:border-emerald-800/30" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(6,182,212,0.03))' }}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 shadow-sm">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">
                        {language === 'fr' ? `Bonjour ${user?.name?.split(' ')[0] || ''}` : `Hello ${user?.name?.split(' ')[0] || ''}`}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        {language === 'fr'
                          ? "Je suis votre assistant juridique. Je connais le Code Penal, le Code Civil, le Code de Procedure Penale et les lois de la RDC."
                          : "I'm your legal assistant. I know the Penal Code, Civil Code, Criminal Procedure Code and DRC laws."}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Suggestions */}
                <div>
                  <p className="text-[11px] text-muted-foreground mb-2.5 flex items-center gap-1.5 font-medium uppercase tracking-wider">
                    <HelpCircle className="w-3 h-3" />
                    {language === 'fr' ? 'Questions courantes' : 'Common questions'}
                  </p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {suggestions.map((q, idx) => (
                      <button 
                        key={idx}
                        className="text-left text-xs px-3.5 py-2.5 rounded-xl border border-border/60 hover:border-emerald-500/40 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 transition-all duration-200 flex items-center gap-2 group"
                        onClick={() => handleSendMessage(q)}
                        data-testid={`suggested-q-${idx}`}
                      >
                        <Send className="w-3 h-3 text-muted-foreground group-hover:text-emerald-600 transition-colors shrink-0" />
                        <span className="truncate">{q}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Chat messages */}
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-2.5 mb-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-1 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-slate-700 to-slate-900 shadow-sm'
                    : 'bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border border-emerald-200/50 dark:border-emerald-800/30'
                }`}>
                  {msg.role === 'user'
                    ? <User className="w-3.5 h-3.5 text-white" />
                    : <Bot className="w-3.5 h-3.5 text-emerald-600" />}
                </div>
                <div className={`group rounded-2xl p-3 max-w-[85%] relative ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-tr-md'
                    : msg.isError
                      ? 'bg-red-50 dark:bg-red-950/20 border border-red-200/50 dark:border-red-800/30 rounded-tl-md'
                      : 'bg-muted/60 border border-border/30 rounded-tl-md'
                }`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose-sm">
                      {renderMessage(msg.content)}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/20">
                        <p className="text-[10px] opacity-40">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <CopyButton text={msg.content} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                      <p className="text-[10px] opacity-40 mt-1.5 text-right">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-2.5 mb-4">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border border-emerald-200/50 dark:border-emerald-800/30 flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <div className="bg-muted/60 border border-border/30 rounded-2xl rounded-tl-md p-3.5">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-emerald-500/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-emerald-500/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-emerald-500/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {language === 'fr' ? 'Analyse en cours...' : 'Analyzing...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* Input area */}
          <div className="shrink-0 p-3 border-t bg-background/90 backdrop-blur-sm">
            <div className="flex gap-2 items-center">
              <div className="flex-1 relative">
                <Input
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={language === 'fr' ? 'Posez votre question juridique...' : 'Ask your legal question...'}
                  disabled={isLoading}
                  className="rounded-xl pr-3 h-10 border-border/60 focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500/50"
                  data-testid="ai-chat-input"
                />
              </div>
              <Button
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputMessage.trim()}
                size="icon"
                className="h-10 w-10 rounded-xl shrink-0 bg-gradient-to-br from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-sm shadow-emerald-500/20"
                data-testid="ai-send-btn"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 text-center opacity-60">
              {language === 'fr' ? 'Reponses a titre informatif uniquement' : 'Informational responses only'}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default LegalAIAssistant;
